import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { Sparkles, ChevronLeft, CheckCircle, Calendar, Clock, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import { HsmBanner } from '../components/HsmBanner';
import DateOfBirthInput from '../components/DateOfBirthInput';
import { formsAPI, academyInfoAPI } from '../lib/api';
import api from '../lib/api';
import SEO from '../components/common/SEO';

const FreeTrial = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClassId = searchParams.get('classId');
  const preselectedClassName = searchParams.get('className');
  const rescheduleTrialId = searchParams.get('reschedule');
  
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [termDates, setTermDates] = useState([]);
  // School-wide blackout dates fetched once. Merged with each class's
  // `blocked_trial_dates` in getTrialDatesForClass so parents never see
  // a date the admin has hidden.
  const [globalBlackouts, setGlobalBlackouts] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  
  // Reschedule mode state
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [rescheduleData, setRescheduleData] = useState(null);
  const [loadingReschedule, setLoadingReschedule] = useState(false);
  const [selectedRescheduleDate, setSelectedRescheduleDate] = useState('');
  const [submittingReschedule, setSubmittingReschedule] = useState(false);
  
  // Initialize form state from sessionStorage if available
  const getSavedFormData = () => {
    try {
      const saved = sessionStorage.getItem('free_trial_form');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to restore form data:', e);
    }
    return null;
  };
  
  const savedForm = getSavedFormData();
  const [formData, setFormData] = useState(savedForm || {
    studentFirstName: '',
    studentMiddleName: '',
    studentLastName: '',
    birthDate: '',
    gender: '',
    streetAddress: '',
    streetAddress2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    guardian1FirstName: '',
    guardian1LastName: '',
    guardian1Email: '',
    guardian1Phone: '',
    guardian2FirstName: '',
    guardian2LastName: '',
    guardian2Email: '',
    guardian2Phone: '',
    course: '',
    classId: preselectedClassId || '',
    medicalConditions: '',
    noMedicalConditions: false,
    // How did you hear about us? (marketing insight — optional)
    hearAboutUs: '',
    hearAboutUsOther: '',
    termsAccepted: false,
    trialDate: '',
    // Multi-class bookings: one entry per selected class
    selections: preselectedClassId
      ? [{ classId: preselectedClassId, className: '', trialDate: '' }]
      : [],
  });
  
  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('free_trial_form', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    fetchClasses();
    fetchTermDates();
    // Fetch the school-wide blackout list (public endpoint).
    api.get('/academy-info/trial-blackout-dates')
      .then((res) => setGlobalBlackouts(res.data?.dates || []))
      .catch(() => setGlobalBlackouts([]));
    
    // Check if this is a reschedule request
    if (rescheduleTrialId) {
      fetchRescheduleData();
    }
  }, []);

  const fetchRescheduleData = async () => {
    setLoadingReschedule(true);
    try {
      const res = await api.get(`/free-trials/${rescheduleTrialId}/details`);
      if (res.data) {
        setRescheduleData(res.data);
        setIsRescheduleMode(true);
      }
    } catch (error) {
      console.error('Failed to fetch reschedule data:', error);
      // If trial not found, just show normal form
      toast.error('Could not find your trial booking. Please fill out the form again.');
    } finally {
      setLoadingReschedule(false);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedRescheduleDate) {
      toast.error('Please select a date');
      return;
    }
    
    setSubmittingReschedule(true);
    try {
      await api.patch(`/free-trials/${rescheduleTrialId}/update-date-public`, {
        trial_date: selectedRescheduleDate
      });
      toast.success('Your trial date has been updated!');
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to update trial date:', error);
      toast.error('Failed to update trial date. Please try again.');
    } finally {
      setSubmittingReschedule(false);
    }
  };

  // Pre-select class when classes are loaded and classId/className is in URL.
  // Coming from a class page means the visitor wants THAT class only — reset any
  // stale selections restored from sessionStorage.
  useEffect(() => {
    if ((preselectedClassId || preselectedClassName) && classes.length > 0) {
      const selectedClass = classes.find(c =>
        c.id === preselectedClassId ||
        (preselectedClassName && (c.name || '').toLowerCase().includes(preselectedClassName.toLowerCase()))
      );
      if (selectedClass) {
        setFormData(prev => ({
          ...prev,
          classId: selectedClass.id,
          course: selectedClass.name,
          selections: [{ classId: selectedClass.id, className: selectedClass.name, trialDate: '' }],
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedClassId, preselectedClassName, classes]);

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      
      // Define custom order for classes
      const classOrder = [
        'upa 3 hour course',
        'primatotterina',
        'junior street',
        'senior street',
        'adult street fitness',
        'adult street choreography'
      ];
      
      // Sort by custom order, then by sort_order, then alphabetically
      const visibleClasses = (res.data || [])
        .filter(c => c.is_visible !== false && c.offers_free_trial !== false)
        .sort((a, b) => {
          const aName = (a.name || '').toLowerCase();
          const bName = (b.name || '').toLowerCase();
          
          // Find index in custom order (partial match)
          const aIndex = classOrder.findIndex(name => aName.includes(name));
          const bIndex = classOrder.findIndex(name => bName.includes(name));
          
          // If both have custom order, sort by that
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only one has custom order, it comes first
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          // Otherwise sort by sort_order then name
          const orderDiff = (a.sort_order || 999) - (b.sort_order || 999);
          if (orderDiff !== 0) return orderDiff;
          return aName.localeCompare(bName);
        });
      setClasses(visibleClasses);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchTermDates = async () => {
    try {
      // Use the trial-dates endpoint that generates Saturdays automatically
      const res = await academyInfoAPI.getTrialDates();
      const dates = res.data || [];
      setTermDates(dates);
    } catch (error) {
      console.error('Failed to fetch trial dates:', error);
      // Fallback: try the old term-dates endpoint
      try {
        const fallbackRes = await academyInfoAPI.getTermDates();
        const now = new Date();
        const futureDates = (fallbackRes.data || [])
          .filter(event => {
            const eventDate = new Date(event.date);
            return eventDate >= now && event.type === 'class';
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 20);
        setTermDates(futureDates);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    } finally {
      setLoadingDates(false);
    }
  };

  // Calculate age from birth date
  const calculateAge = (birthDateStr) => {
    if (!birthDateStr) return null;
    const birth = new Date(birthDateStr);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Check if student is an adult (18+)
  const isAdult = calculateAge(formData.birthDate) >= 18;

  // Parse term_dates string to extract start and end dates
  const parseTermDates = (termDatesStr) => {
    if (!termDatesStr) return { start: null, end: null };
    
    const months = {
      'jan': 0, 'january': 0,
      'feb': 1, 'february': 1,
      'mar': 2, 'march': 2,
      'apr': 3, 'april': 3,
      'may': 4,
      'jun': 5, 'june': 5,
      'jul': 6, 'july': 6,
      'aug': 7, 'august': 7,
      'sep': 8, 'september': 8,
      'oct': 9, 'october': 9,
      'nov': 10, 'november': 10,
      'dec': 11, 'december': 11
    };
    
    // Remove ordinal suffixes (st, nd, rd, th)
    const cleanStr = termDatesStr.replace(/(\d+)(st|nd|rd|th)/gi, '$1');
    
    // Try format: "April 23 - July 23" or "April 23 - July 23, 2026"
    const pattern1 = /([a-zA-Z]+)\s*(\d{1,2})\s*-\s*([a-zA-Z]+)\s*(\d{1,2})(?:,?\s*(\d{4}))?/i;
    const match1 = cleanStr.match(pattern1);
    
    if (match1) {
      const startMonth = months[match1[1].toLowerCase()];
      const startDay = parseInt(match1[2]);
      const endMonth = months[match1[3].toLowerCase()];
      const endDay = parseInt(match1[4]);
      const year = match1[5] ? parseInt(match1[5]) : new Date().getFullYear();
      
      if (startMonth !== undefined && endMonth !== undefined) {
        // Handle year rollover (e.g., Nov - Feb)
        let endYear = year;
        if (endMonth < startMonth) {
          endYear = year + 1;
        }
        
        return {
          start: new Date(year, startMonth, startDay),
          end: new Date(endYear, endMonth, endDay)
        };
      }
    }
    
    // Try format: "23 April - 23 July" or "6 Jan - 15 Mar 2026"
    const pattern2 = /(\d{1,2})\s*([a-zA-Z]+)\s*-\s*(\d{1,2})\s*([a-zA-Z]+)(?:,?\s*(\d{4}))?/i;
    const match2 = cleanStr.match(pattern2);
    
    if (match2) {
      const startDay = parseInt(match2[1]);
      const startMonth = months[match2[2].toLowerCase()];
      const endDay = parseInt(match2[3]);
      const endMonth = months[match2[4].toLowerCase()];
      const year = match2[5] ? parseInt(match2[5]) : new Date().getFullYear();
      
      if (startMonth !== undefined && endMonth !== undefined) {
        let endYear = year;
        if (endMonth < startMonth) {
          endYear = year + 1;
        }
        
        return {
          start: new Date(year, startMonth, startDay),
          end: new Date(endYear, endMonth, endDay)
        };
      }
    }
    
    return { start: null, end: null };
  };

  // Generate trial dates based on a given class's day_of_week and term_dates
  const getTrialDatesForClass = (classIdOverride) => {
    const lookupId = classIdOverride !== undefined ? classIdOverride : formData.classId;
    const selectedClass = classes.find(c => c.id === lookupId);
    if (!selectedClass) {
      return termDates.filter(d => !(globalBlackouts || []).includes(d.date));
    }
    
    // Map day names to day numbers (0 = Sunday, 6 = Saturday)
    const dayMap = {
      'sunday': 0, 'sun': 0, 'sundays': 0,
      'monday': 1, 'mon': 1, 'mondays': 1,
      'tuesday': 2, 'tue': 2, 'tuesdays': 2,
      'wednesday': 3, 'wed': 3, 'wednesdays': 3,
      'thursday': 4, 'thu': 4, 'thursdays': 4,
      'friday': 5, 'fri': 5, 'fridays': 5,
      'saturday': 6, 'sat': 6, 'saturdays': 6
    };

    // If no day_of_week set, fall back to Saturday dates
    if (!selectedClass.day_of_week) {
      return termDates.filter(d => !(globalBlackouts || []).includes(d.date));
    }

    let dayStr = selectedClass.day_of_week.toLowerCase().split(/[&,\s]+/)[0].trim();
    let targetDay = dayMap[dayStr];
    // Forgiving match: strip trailing 's' if not found
    if (targetDay === undefined && dayStr.endsWith('s')) {
      targetDay = dayMap[dayStr.slice(0, -1)];
    }
    
    if (targetDay === undefined) {
      return termDates.filter(d => !(globalBlackouts || []).includes(d.date));
    }

    // Parse term dates to get date range
    const { start: termStart, end: termEnd } = parseTermDates(selectedClass.term_dates);

    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Ignore a stale term window that has already finished (e.g. summer break
    // before the new term dates are entered) — otherwise zero dates generate
    // and the picker used to wrongly fall back to a generic every-day list.
    const termOver = termEnd && termEnd < today;
    const minDate = (!termOver && termStart) ? termStart : today;
    const maxDate = (!termOver && termEnd) ? termEnd : null;

    let current = minDate > today ? new Date(minDate) : new Date(today);
    // Move to next occurrence of target day
    while (current.getDay() !== targetDay) {
      current.setDate(current.getDate() + 1);
    }

    // Admin-controlled filters: per-class blocklist + school-wide blackouts.
    const perClassBlocked = new Set(selectedClass.blocked_trial_dates || []);
    const globalBlocked = new Set(globalBlackouts || []);

    // Look ahead up to 26 weeks so long blackout periods (holidays) still
    // surface the first dates after the break; stop at 10 selectable dates.
    for (let i = 0; i < 26 && dates.length < 10; i++) {
      if (maxDate && current > maxDate) break;
      if (current >= today) {
        // Format date as YYYY-MM-DD in local time (not UTC)
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        if (!perClassBlocked.has(dateStr) && !globalBlocked.has(dateStr)) {
          dates.push({
            date: dateStr,
            title: `${selectedClass.name} - ${selectedClass.day_of_week}${selectedClass.start_time ? ` ${selectedClass.start_time}` : ''}`
          });
        }
      }
      current.setDate(current.getDate() + 7);
    }

    // Never fall back to the generic daily list for a class with a known
    // weekday — an empty list shows the "no dates available" notice instead.
    return dates;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Multi-class helpers
  const toggleClassSelection = (cls) => {
    setFormData(prev => {
      const exists = prev.selections.some(s => s.classId === cls.id);
      if (exists) {
        return { ...prev, selections: prev.selections.filter(s => s.classId !== cls.id) };
      }
      return {
        ...prev,
        selections: [...prev.selections, { classId: cls.id, className: cls.name, trialDate: '' }],
      };
    });
  };

  const setSelectionDate = (classId, date) => {
    setFormData(prev => ({
      ...prev,
      selections: prev.selections.map(s =>
        s.classId === classId ? { ...s, trialDate: date } : s
      ),
    }));
  };

  const isClassSelected = (classId) => formData.selections.some(s => s.classId === classId);
  const getSelection = (classId) => formData.selections.find(s => s.classId === classId);

  const nextStep = () => {
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate medical conditions - must either fill in or tick checkbox
    if (!formData.medicalConditions.trim() && !formData.noMedicalConditions) {
      toast.error('Please either fill in the medical conditions or tick "No known medical conditions"');
      return;
    }
    
    if (!formData.termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    // Referral source is now required — helps us know which channels bring
    // new families in. "Other" needs the free-text field filled too.
    if (!(formData.hearAboutUs || '').trim()) {
      toast.error('Please tell us how you heard about us');
      return;
    }
    if (formData.hearAboutUs === 'Other' && !formData.hearAboutUsOther.trim()) {
      toast.error('Please tell us where you heard about us');
      return;
    }

    // Validate at least one class and each has a date
    if (!formData.selections || formData.selections.length === 0) {
      toast.error('Please select at least one class to trial');
      return;
    }
    const missingDate = formData.selections.find(s => !s.trialDate);
    if (missingDate) {
      toast.error(`Please pick a trial date for ${missingDate.className || 'each selected class'}`);
      return;
    }
    
    setSubmitting(true);
    
    try {
      const medical = formData.noMedicalConditions ? 'None' : formData.medicalConditions;
      // Ensure each selection carries its class name (fill from classes array if missing)
      const selections = formData.selections.map(s => {
        const cls = classes.find(c => c.id === s.classId);
        return {
          classId: s.classId,
          className: s.className || cls?.name || '',
          trialDate: s.trialDate,
        };
      });

      const submitData = {
        studentFirstName: formData.studentFirstName,
        studentMiddleName: formData.studentMiddleName,
        studentLastName: formData.studentLastName,
        birthDate: formData.birthDate,
        gender: formData.gender,
        streetAddress: formData.streetAddress,
        streetAddress2: formData.streetAddress2,
        city: formData.city,
        postcode: formData.postcode,
        country: formData.country,
        guardian1FirstName: formData.guardian1FirstName,
        guardian1LastName: formData.guardian1LastName,
        guardian1Email: formData.guardian1Email,
        guardian1Phone: formData.guardian1Phone,
        guardian2FirstName: formData.guardian2FirstName,
        guardian2LastName: formData.guardian2LastName,
        guardian2Email: formData.guardian2Email,
        guardian2Phone: formData.guardian2Phone,
        medicalConditions: medical,
        hearAboutUs: formData.hearAboutUs || '',
        hearAboutUsOther: formData.hearAboutUs === 'Other' ? (formData.hearAboutUsOther || '') : '',
        selections,
      };
      const response = await formsAPI.submitFreeTrialMulti(submitData);
      
      // Clear saved form data after successful submission
      sessionStorage.removeItem('free_trial_form');

      // GTM conversion event — fires only after a genuinely successful booking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'free_trial_booked' });
      
      setSubmitted(true);
      toast.success(response.data.message || 'Free trials booked successfully!');
    } catch (error) {
      console.error('Free trial form error:', error);
      toast.error('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SEO page="freeTrial" title="Trial Booked | Universal Performing Arts" />
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-16">
          <Card className="shadow-xl border-[#333] bg-[#111] text-center">
            <CardContent className="p-8">
              <div className="w-20 h-20 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {isRescheduleMode ? 'Trial Date Updated!' : 'Trial Booked!'}
              </h1>
              <p className="text-white/60 mb-6">
                {isRescheduleMode 
                  ? 'Your trial date has been successfully updated. We look forward to seeing you!'
                  : 'Thank you for booking your free trial at Universal Performing Arts! We\'ll be in touch shortly with more details.'
                }
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/')} className="bg-[#FF007F] hover:bg-[#D6006A]">
                  Back to Home
                </Button>
                <Button variant="outline" onClick={() => navigate('/classes')} className="border-[#333] text-white hover:bg-[#222]">
                  View Classes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reschedule Mode - Show date picker with class-specific dates
  if (isRescheduleMode && rescheduleData) {
    // Find the class that matches the class_interest to get day_of_week
    const matchedClass = classes.find(c => 
      c.name?.toLowerCase().includes(rescheduleData.class_interest?.toLowerCase()) ||
      rescheduleData.class_interest?.toLowerCase().includes(c.name?.toLowerCase())
    );
    
    // Generate class-specific dates using the same logic as main form
    const getRescheduleDates = () => {
      // If classes not loaded yet, return empty
      if (loadingClasses || loadingDates) {
        return [];
      }
      
      // If no matched class or no day_of_week, use Saturday dates (termDates)
      if (!matchedClass || !matchedClass.day_of_week) {
        // Transform termDates to the expected format
        return termDates.map(d => ({
          date: d.date,
          display: d.description || d.title || new Date(d.date).toLocaleDateString('en-GB', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          }),
          className: matchedClass?.name || rescheduleData.class_interest,
          time: matchedClass?.start_time || ''
        }));
      }
      
      const dayMap = {
        'sunday': 0, 'sun': 0,
        'monday': 1, 'mon': 1,
        'tuesday': 2, 'tue': 2,
        'wednesday': 3, 'wed': 3,
        'thursday': 4, 'thu': 4,
        'friday': 5, 'fri': 5,
        'saturday': 6, 'sat': 6
      };

      const dayStr = matchedClass.day_of_week.toLowerCase().split(/[&,\s]+/)[0].trim();
      const targetDay = dayMap[dayStr];
      
      if (targetDay === undefined) {
        return termDates.map(d => ({
          date: d.date,
          display: d.description || d.title || new Date(d.date).toLocaleDateString('en-GB', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          }),
          className: matchedClass.name,
          time: matchedClass.start_time || ''
        }));
      }

      // Generate dates for the next 12 weeks on the class day
      const dates = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let current = new Date(today);
      // Move to next target day
      while (current.getDay() !== targetDay) {
        current.setDate(current.getDate() + 1);
      }
      
      // Generate 12 weeks of dates
      for (let i = 0; i < 12; i++) {
        if (current > today) {
          dates.push({
            date: current.toISOString().split('T')[0],
            display: current.toLocaleDateString('en-GB', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            }),
            className: matchedClass.name,
            time: matchedClass.start_time || ''
          });
        }
        current.setDate(current.getDate() + 7);
      }
      
      return dates;
    };
    
    const rescheduleDates = getRescheduleDates();
    
    // Show loading if data not ready
    if (loadingClasses || loadingDates) {
      return (
        <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white/60">Loading available dates...</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SEO page="freeTrial" title="Select Trial Date | Universal Performing Arts" />
        <Navbar />
        <Toaster position="top-right" richColors />
        
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="shadow-xl border-[#333] bg-[#111]">
            <CardHeader className="text-center border-b border-[#333]">
              <div className="w-16 h-16 bg-[#FF007F]/20 border border-[#FF007F]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-[#FF007F]" />
              </div>
              <CardTitle className="font-heading text-2xl text-white">
                Select Your Trial Date
              </CardTitle>
              <p className="text-white/60 mt-2">
                Please choose a date for {rescheduleData.student_name}'s free trial.
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Class Info */}
              {matchedClass && (
                <div className="bg-gradient-to-r from-[#FF007F]/10 to-[#7000FF]/10 rounded-lg p-4 border border-[#FF007F]/30">
                  <h3 className="text-white font-semibold text-lg">{matchedClass.name}</h3>
                  <div className="flex items-center gap-2 text-white/70 text-sm mt-1">
                    <Clock className="w-4 h-4 text-[#FF007F]" />
                    <span>{matchedClass.day_of_week} {matchedClass.start_time} - {matchedClass.end_time}</span>
                  </div>
                  {matchedClass.location && (
                    <div className="flex items-center gap-2 text-white/70 text-sm mt-1">
                      <MapPin className="w-4 h-4 text-[#FF007F]" />
                      <span>{matchedClass.location}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Date Selection - Card Grid Style */}
              <div className="space-y-3">
                <Label className="text-white/70 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#FF007F]" />
                  Select Your Trial Date *
                </Label>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-2">
                  {rescheduleDates.map((dateOption) => (
                    <div
                      key={dateOption.date}
                      onClick={() => setSelectedRescheduleDate(dateOption.date)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedRescheduleDate === dateOption.date
                          ? 'bg-[#FF007F]/20 border-[#FF007F] ring-2 ring-[#FF007F]/50'
                          : 'bg-[#1a1a1a] border-[#333] hover:border-[#FF007F]/50 hover:bg-[#222]'
                      }`}
                    >
                      <p className={`font-semibold ${
                        selectedRescheduleDate === dateOption.date ? 'text-[#FF007F]' : 'text-white'
                      }`}>
                        {dateOption.display}
                      </p>
                      {dateOption.className && (
                        <p className="text-white/60 text-sm mt-1">
                          {dateOption.className} {dateOption.time ? `- ${dateOption.time}` : ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                
                {rescheduleDates.length === 0 && (
                  <p className="text-white/50 text-center py-4">No available dates found. Please contact us.</p>
                )}
              </div>
              
              <Button
                onClick={handleRescheduleSubmit}
                disabled={submittingReschedule || !selectedRescheduleDate}
                className="w-full h-12 bg-gradient-to-r from-[#FF007F] to-[#FF6B35] hover:from-[#D6006A] hover:to-[#E55A2B] text-white font-bold disabled:opacity-50"
              >
                {submittingReschedule ? 'Updating...' : 'Confirm Date'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading reschedule data
  if (rescheduleTrialId && loadingReschedule) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/60">Loading your trial details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO page="freeTrial" />
      <Navbar />
      <HsmBanner />
      <Toaster position="top-right" richColors />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4 text-white/60 hover:text-white hover:bg-white/10"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        <Card className="shadow-xl border-[#333] bg-[#111]">
          <CardHeader className="text-center border-b border-[#333] bg-gradient-to-r from-[#7000FF]/20 to-[#FF007F]/20">
            <div className="w-16 h-16 bg-gradient-to-br from-[#7000FF] to-[#FF007F] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="font-heading text-2xl text-white">Book Your Free Trial</CardTitle>
            <p className="text-white/60 text-sm mt-2">
              Experience UPA with a complimentary trial session!
            </p>
            
            {/* Progress Steps */}
            <div className="flex justify-center gap-2 mt-6">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-3 h-3 rounded-full ${
                    s === step ? 'bg-[#FF007F]' : s < step ? 'bg-green-500' : 'bg-[#333]'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-white/50 mt-2">Step {step} of 4</p>
          </CardHeader>
          
          <CardContent className="p-6">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Student Info */}
              {step === 1 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg mb-4 text-white">Student Information</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/80">First Name *</Label>
                      <Input
                        value={formData.studentFirstName}
                        onChange={(e) => handleChange('studentFirstName', e.target.value)}
                        required
                        className="bg-[#1a1a1a] border-[#333] text-white"
                        data-testid="student-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/80">Middle Name</Label>
                      <Input
                        value={formData.studentMiddleName}
                        onChange={(e) => handleChange('studentMiddleName', e.target.value)}
                        className="bg-[#1a1a1a] border-[#333] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/80">Last Name *</Label>
                      <Input
                        value={formData.studentLastName}
                        onChange={(e) => handleChange('studentLastName', e.target.value)}
                        required
                        className="bg-[#1a1a1a] border-[#333] text-white"
                        data-testid="student-last-name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <DateOfBirthInput
                      value={formData.birthDate}
                      onChange={(v) => handleChange('birthDate', v)}
                      label="Date of Birth"
                      required
                    />
                    <div className="space-y-2">
                      <Label className="text-white/80">Gender *</Label>
                      <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                        <SelectTrigger className="bg-[#1a1a1a] border-[#333] text-white" data-testid="gender-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-[#333]">
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="na">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Multi-class selection */}
                  <div className="space-y-3 border-t border-[#333] pt-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-white/80">Select classes to trial *</Label>
                      <span className="text-xs text-white/50">
                        {formData.selections.length} selected
                      </span>
                    </div>
                    <p className="text-xs text-white/50">
                      Pick any combination — you can trial multiple classes.
                    </p>
                    {loadingClasses ? (
                      <div className="text-white/50 text-sm py-2">Loading classes...</div>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto p-1" data-testid="class-multi-list">
                        {classes.map(cls => {
                          const selected = isClassSelected(cls.id);
                          return (
                            <div
                              key={cls.id}
                              className={`rounded-lg border transition-all ${
                                selected
                                  ? 'bg-[#FF007F]/10 border-[#FF007F]'
                                  : 'bg-[#1a1a1a] border-[#333] hover:border-[#FF007F]/50'
                              }`}
                            >
                              <label
                                className="flex items-start gap-3 p-3 cursor-pointer"
                                data-testid={`class-toggle-${cls.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleClassSelection(cls)}
                                  className="mt-1 accent-[#FF007F] w-4 h-4 cursor-pointer"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-white font-medium">{cls.name}</p>
                                  <p className="text-xs text-white/50 mt-0.5">
                                    {cls.day_of_week && `${cls.day_of_week}`}
                                    {cls.start_time && ` ${cls.start_time}`}
                                    {cls.end_time && ` - ${cls.end_time}`}
                                    {cls.address && ` • ${cls.address}`}
                                  </p>
                                  {(cls.age_min || cls.age_max) && (
                                    <p className="text-xs text-white/40">Ages {cls.age_min}-{cls.age_max}</p>
                                  )}
                                </div>
                              </label>

                              {/* Per-class date picker — only shown when selected */}
                              {selected && (
                                <div className="border-t border-[#FF007F]/20 p-3 bg-black/20">
                                  <Label className="text-white/80 text-xs flex items-center gap-1 mb-2">
                                    <Calendar className="w-3 h-3 text-[#FF007F]" />
                                    Pick a trial date for {cls.name}
                                  </Label>
                                  {(() => {
                                    const availableDates = getTrialDatesForClass(cls.id);
                                    const selValue = getSelection(cls.id)?.trialDate || '';
                                    // If the class has a schedule but every candidate date is blocked
                                    // by admin, show a friendly notice instead of a free date input.
                                    if (availableDates.length === 0 && cls.day_of_week && cls.term_dates) {
                                      return (
                                        <p
                                          className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2"
                                          data-testid={`no-trial-dates-${cls.id}`}
                                        >
                                          No trial dates currently available for this class. Please email theteam@upaschool.com and we&apos;ll find you a slot.
                                        </p>
                                      );
                                    }
                                    if (availableDates.length === 0) {
                                      return (
                                        <Input
                                          type="date"
                                          value={selValue}
                                          onChange={(e) => setSelectionDate(cls.id, e.target.value)}
                                          className="bg-[#222] border-[#333] text-white"
                                          min={new Date().toISOString().split('T')[0]}
                                          data-testid={`class-date-${cls.id}`}
                                        />
                                      );
                                    }
                                    return (
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                                        {availableDates.map((event, idx) => (
                                          <div
                                            key={idx}
                                            onClick={() => setSelectionDate(cls.id, event.date)}
                                            className={`p-2 rounded border cursor-pointer text-xs transition-all ${
                                              selValue === event.date
                                                ? 'bg-[#FF007F]/30 border-[#FF007F]'
                                                : 'bg-[#1a1a1a] border-[#333] hover:border-[#FF007F]/50'
                                            }`}
                                            data-testid={`trial-date-${cls.id}-${idx}`}
                                          >
                                            <p className="text-white font-medium">
                                              {formatDate(event.date)}
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                  {getSelection(cls.id)?.trialDate && (
                                    <p className="text-[#FF007F] text-xs mt-2">
                                      ✓ {formatDate(getSelection(cls.id).trialDate)}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Address */}
              {step === 2 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg mb-4 text-white">Address</h3>
                  
                  <div className="space-y-2">
                    <Label className="text-white/80">Street Address</Label>
                    <Input
                      value={formData.streetAddress}
                      onChange={(e) => handleChange('streetAddress', e.target.value)}
                      placeholder="Street address"
                      className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white/80">Street Address Line 2</Label>
                    <Input
                      value={formData.streetAddress2}
                      onChange={(e) => handleChange('streetAddress2', e.target.value)}
                      placeholder="Apartment, suite, etc."
                      className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/80">City</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        className="bg-[#1a1a1a] border-[#333] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/80">Postcode</Label>
                      <Input
                        value={formData.postcode}
                        onChange={(e) => handleChange('postcode', e.target.value)}
                        className="bg-[#1a1a1a] border-[#333] text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white/80">Country</Label>
                    <Select value={formData.country} onValueChange={(v) => handleChange('country', v)}>
                      <SelectTrigger className="bg-[#1a1a1a] border-[#333] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#333]">
                        <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                        <SelectItem value="Ireland">Ireland</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Step 3: Contact Details (Guardian for children, Self for adults) */}
              {step === 3 && (
                <div className="space-y-6">
                  {isAdult ? (
                    /* Adult booking - contact details for themselves */
                    <div>
                      <h3 className="font-semibold text-lg mb-4 text-white">Your Contact Details *</h3>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <Label className="text-white/80">First Name *</Label>
                          <Input
                            value={formData.guardian1FirstName}
                            onChange={(e) => handleChange('guardian1FirstName', e.target.value)}
                            required
                            className="bg-[#1a1a1a] border-[#333] text-white"
                            data-testid="contact-first-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/80">Last Name *</Label>
                          <Input
                            value={formData.guardian1LastName}
                            onChange={(e) => handleChange('guardian1LastName', e.target.value)}
                            required
                            className="bg-[#1a1a1a] border-[#333] text-white"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-white/80">Email *</Label>
                          <Input
                            type="email"
                            value={formData.guardian1Email}
                            onChange={(e) => handleChange('guardian1Email', e.target.value)}
                            required
                            className="bg-[#1a1a1a] border-[#333] text-white"
                            data-testid="contact-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/80">Phone *</Label>
                          <Input
                            type="tel"
                            value={formData.guardian1Phone}
                            onChange={(e) => handleChange('guardian1Phone', e.target.value)}
                            required
                            className="bg-[#1a1a1a] border-[#333] text-white"
                            data-testid="contact-phone"
                          />
                        </div>
                      </div>
                      
                      <p className="text-white/50 text-sm mt-4">
                        We'll use these details to send you trial reminders and class information.
                      </p>
                    </div>
                  ) : (
                    /* Child booking - guardian details required */
                    <>
                      <div>
                        <h3 className="font-semibold text-lg mb-4 text-white">Parent/Guardian 1 (Primary Contact) *</h3>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label className="text-white/80">First Name *</Label>
                            <Input
                              value={formData.guardian1FirstName}
                              onChange={(e) => handleChange('guardian1FirstName', e.target.value)}
                              required
                              className="bg-[#1a1a1a] border-[#333] text-white"
                              data-testid="guardian1-first-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white/80">Last Name *</Label>
                            <Input
                              value={formData.guardian1LastName}
                              onChange={(e) => handleChange('guardian1LastName', e.target.value)}
                              required
                              className="bg-[#1a1a1a] border-[#333] text-white"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-white/80">Email *</Label>
                            <Input
                              type="email"
                              value={formData.guardian1Email}
                              onChange={(e) => handleChange('guardian1Email', e.target.value)}
                              required
                              className="bg-[#1a1a1a] border-[#333] text-white"
                              data-testid="guardian1-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white/80">Phone *</Label>
                            <Input
                              type="tel"
                              value={formData.guardian1Phone}
                              onChange={(e) => handleChange('guardian1Phone', e.target.value)}
                              required
                              className="bg-[#1a1a1a] border-[#333] text-white"
                              data-testid="guardian1-phone"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="border-t border-[#333] pt-6">
                        <h3 className="font-semibold text-lg mb-4 text-white">Parent/Guardian 2 (Optional)</h3>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label className="text-white/80">First Name</Label>
                            <Input
                              value={formData.guardian2FirstName}
                              onChange={(e) => handleChange('guardian2FirstName', e.target.value)}
                              className="bg-[#1a1a1a] border-[#333] text-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white/80">Last Name</Label>
                            <Input
                              value={formData.guardian2LastName}
                              onChange={(e) => handleChange('guardian2LastName', e.target.value)}
                              className="bg-[#1a1a1a] border-[#333] text-white"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-white/80">Email</Label>
                            <Input
                              type="email"
                              value={formData.guardian2Email}
                              onChange={(e) => handleChange('guardian2Email', e.target.value)}
                              className="bg-[#1a1a1a] border-[#333] text-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white/80">Phone</Label>
                            <Input
                              type="tel"
                              value={formData.guardian2Phone}
                              onChange={(e) => handleChange('guardian2Phone', e.target.value)}
                              className="bg-[#1a1a1a] border-[#333] text-white"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 4: Medical & Terms */}
              {step === 4 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-4 text-white">Medical Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-white/80">Medical Conditions, Allergies or Special Requirements *</Label>
                        <Textarea
                          value={formData.medicalConditions}
                          onChange={(e) => {
                            handleChange('medicalConditions', e.target.value);
                            if (e.target.value.trim()) {
                              handleChange('noMedicalConditions', false);
                            }
                          }}
                          placeholder="Please list any medical conditions, allergies, or special requirements we should know about..."
                          rows={4}
                          className={`bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40 ${formData.noMedicalConditions ? 'opacity-50' : ''}`}
                          data-testid="medical-conditions"
                          disabled={formData.noMedicalConditions}
                        />
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="no-medical"
                          checked={formData.noMedicalConditions}
                          onCheckedChange={(checked) => {
                            handleChange('noMedicalConditions', checked);
                            if (checked) {
                              handleChange('medicalConditions', '');
                            }
                          }}
                          className="border-[#333] data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                          data-testid="no-medical-checkbox"
                        />
                        <Label htmlFor="no-medical" className="text-white/80 cursor-pointer">
                          No known medical conditions, allergies or special requirements
                        </Label>
                      </div>
                      
                      {!formData.medicalConditions.trim() && !formData.noMedicalConditions && (
                        <p className="text-amber-400 text-sm">
                          Please either fill in the medical conditions field or tick the checkbox above
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="border-t border-[#333] pt-6">
                    <h3 className="font-semibold text-lg mb-4 text-white">
                      How did you hear about us? <span className="text-[#FF007F]">*</span>
                    </h3>
                    <div className="space-y-3">
                      <Select
                        value={formData.hearAboutUs || ''}
                        onValueChange={(v) => {
                          handleChange('hearAboutUs', v);
                          if (v !== 'Other') handleChange('hearAboutUsOther', '');
                        }}
                      >
                        <SelectTrigger
                          className="bg-[#1a1a1a] border-[#333] text-white"
                          data-testid="heard-about-us-trigger"
                        >
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-[#333] text-white">
                          <SelectItem value="Google" data-testid="heard-about-us-google">Google</SelectItem>
                          <SelectItem value="Instagram" data-testid="heard-about-us-instagram">Instagram</SelectItem>
                          <SelectItem value="Facebook" data-testid="heard-about-us-facebook">Facebook</SelectItem>
                          <SelectItem value="Word of mouth" data-testid="heard-about-us-word-of-mouth">Word of mouth</SelectItem>
                          <SelectItem value="Other" data-testid="heard-about-us-other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.hearAboutUs === 'Other' && (
                        <Input
                          value={formData.hearAboutUsOther}
                          onChange={(e) => handleChange('hearAboutUsOther', e.target.value)}
                          placeholder="Please tell us where..."
                          className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/40"
                          data-testid="heard-about-us-other-input"
                        />
                      )}
                    </div>
                  </div>

                  <div className="border-t border-[#333] pt-6">
                    <h3 className="font-semibold text-lg mb-4 text-white">Terms &amp; Conditions</h3>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="terms"
                        checked={formData.termsAccepted}
                        onCheckedChange={(checked) => handleChange('termsAccepted', checked)}
                        className="border-[#333] data-[state=checked]:bg-[#FF007F] data-[state=checked]:border-[#FF007F]"
                        data-testid="terms-checkbox"
                      />
                      <label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer text-white/80">
                        I confirm that I have read and agree to the{' '}
                        <a
                          href="/terms-and-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#FF007F] underline hover:no-underline"
                          data-testid="terms-link"
                        >
                          UPA Terms and Conditions
                        </a>
                        , available on the UPA website, including the six-week notice period for cancellation and UPA's policies regarding attendance, conduct, safeguarding, photography, filming and fees. <span className="text-[#FF007F]">*</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-[#333]">
                {step > 1 ? (
                  <Button type="button" variant="outline" onClick={prevStep} className="border-[#333] text-white hover:bg-[#222]">
                    Previous
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 4 ? (
                  <Button type="button" onClick={nextStep} className="bg-[#FF007F] hover:bg-[#D6006A]">
                    Next
                  </Button>
                ) : (
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="bg-[#FF007F] hover:bg-[#D6006A]"
                    data-testid="submit-trial-btn"
                  >
                    {submitting ? 'Submitting...' : 'Book Free Trial'}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FreeTrial;
